import type { AppBrand } from "./types";

const STYLE_ELEMENT_ID = "app-branding-tokens";

const SIDEBAR_GRADIENT_LIGHTNESS = [9, 13, 18];
const SIDEBAR_GRADIENT_SATURATION = [45, 45, 50];

const WHITE = "0 0% 100%";
const NEAR_BLACK = "0 0% 10%";

export const SIDEBAR_CONTRAST_FLOOR = 7;

type Hsl = { h: number; s: number; l: number };

export const hexToHsl = (hex: string): Hsl => {
  const { r, g, b } = hexToRgb(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return { h: 0, s: 0, l: l * 100 };

  const s = delta / (1 - Math.abs(2 * l - 1));

  let h: number;
  if (max === r) h = ((g - b) / delta) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  return { h: (((h * 60) % 360) + 360) % 360, s: s * 100, l: l * 100 };
};

/** Relative luminance per WCAG 2.1. Hue-weighted, so it is the only safe basis
    for a contrast check — HSL lightness rates gold and blue identically. */
export const relativeLuminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  const channel = (value: number) =>
    value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

export const contrastRatio = (hexA: string, hexB: string): number => {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
};

/** Whether near-white nav text and icons stay legible on this gradient stop. */
export const hasLegibleNavContrast = (hex: string): boolean =>
  contrastRatio(hex, "#FFFFFF") >= SIDEBAR_CONTRAST_FLOOR;

/** Light brands cannot carry white button text, so the foreground flips rather
    than the brand colour being rejected. */
export const readableForegroundOn = (hex: string): string =>
  contrastRatio(hex, "#FFFFFF") >= contrastRatio(hex, "#1A1A1A")
    ? "#FFFFFF"
    : "#1A1A1A";

/** The token form of `readableForegroundOn`, which is how consumers read it. */
export const foregroundFor = (hex: string): string =>
  readableForegroundOn(hex) === "#FFFFFF" ? WHITE : NEAR_BLACK;

export const buildAppBrandingTokens = (
  branding: AppBrand,
): Record<string, string> => {
  const { primary } = branding;
  const { h, s, l } = hexToHsl(primary);

  const primaryToken = triple(h, s, l);
  const primaryForeground = foregroundFor(primary);

  const gradientStops = [
    branding.gradient?.from,
    branding.gradient?.via,
    branding.gradient?.to,
  ].map((stop, index) =>
    stop
      ? tripleFromHex(stop)
      : triple(
          h,
          SIDEBAR_GRADIENT_SATURATION[index],
          SIDEBAR_GRADIENT_LIGHTNESS[index],
        ),
  );

  return {
    "--primary": primaryToken,
    "--primary-foreground": primaryForeground,
    "--primary-surface": triple(h, s, 94),
    "--primary-content": triple(h, s * 0.75, 20),
    "--primary-border": triple(h, s, 86),
    "--primary-gradient-from": triple(h, s, clamp(l + 13)),
    "--primary-gradient-to": triple(h, s, clamp(l - 6)),
    "--citation-accent": primaryToken,
    "--ring": primaryToken,
    "--sidebar-primary": primaryToken,
    "--sidebar-primary-foreground": primaryForeground,
    "--sidebar-ring": primaryToken,
    "--sidebar-gradient-from": gradientStops[0],
    "--sidebar-gradient-via": gradientStops[1],
    "--sidebar-gradient-to": gradientStops[2],
  };
};

/** Emitted for both `:root` and `.dark`: the sidebar wrapper carries a literal
    `.dark` class, so a `:root`-only block leaves the nav on the default
    gradient while still looking correct against `:root` in devtools. */
export const buildAppBrandingCss = (branding: AppBrand): string => {
  const declarations = Object.entries(buildAppBrandingTokens(branding))
    .map(([token, value]) => `  ${token}: ${value};`)
    .join("\n");

  return [`:root {\n${declarations}\n}`, `.dark {\n${declarations}\n}`].join(
    "\n\n",
  );
};

export const injectAppBrandingCss = (branding: AppBrand): void => {
  const style = getStyleElement();
  style.textContent = buildAppBrandingCss(branding);
  document.head.append(style);
};

export const removeAppBrandingCss = (): void => {
  document.getElementById(STYLE_ELEMENT_ID)?.remove();
};

const getStyleElement = (): HTMLStyleElement => {
  const existing = document.getElementById(STYLE_ELEMENT_ID);
  if (existing) return existing as HTMLStyleElement;

  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  document.head.append(style);
  return style;
};

const hexToRgb = (hex: string) => {
  const value = hex.replace("#", "");
  return {
    r: parseInt(value.slice(0, 2), 16) / 255,
    g: parseInt(value.slice(2, 4), 16) / 255,
    b: parseInt(value.slice(4, 6), 16) / 255,
  };
};

const clamp = (value: number) => Math.min(100, Math.max(0, value));

const round = (value: number) => Math.round(value * 10) / 10;

const triple = (h: number, s: number, l: number) =>
  `${round(h)} ${round(clamp(s))}% ${round(clamp(l))}%`;

export const tripleFromHex = (hex: string) => {
  const { h, s, l } = hexToHsl(hex);
  return triple(h, s, l);
};

const hslToHex = ({ h, s, l }: Hsl): string => {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const secondary = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const offset = lightness - chroma / 2;

  const [r, g, b] = (
    [
      [chroma, secondary, 0],
      [secondary, chroma, 0],
      [0, chroma, secondary],
      [0, secondary, chroma],
      [secondary, 0, chroma],
      [chroma, 0, secondary],
    ] as const
  )[Math.min(5, Math.floor(h / 60))];

  return `#${[r, g, b]
    .map((value) =>
      Math.round((value + offset) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`.toUpperCase();
};

/** The gradient the nav gets when the brand supplies no stops of its own —
    shown in the editor so it is never applied sight unseen. */
export const derivedGradientHexes = (
  primaryHex: string,
): [string, string, string] => {
  const { h } = hexToHsl(primaryHex);
  return SIDEBAR_GRADIENT_LIGHTNESS.map((lightness, index) =>
    hslToHex({ h, s: SIDEBAR_GRADIENT_SATURATION[index], l: lightness }),
  ) as [string, string, string];
};
